// src/pages/Rider.js
import { useState, useEffect } from "react";
import { getRiderShipments, updateShipmentStatus } from "../services/api";

const statusStyle = s => ({
  delivered:        { color: "#16A34A", bg: "#DCFCE7" },
  in_transit:       { color: "#00A896", bg: "rgba(0,198,215,.1)" },
  out_for_delivery: { color: "#00A896", bg: "rgba(0,198,215,.1)" },
  pending:          { color: "#D97706", bg: "#FEF3C7" },
  picked_up:        { color: "#D97706", bg: "#FEF3C7" },
}[s] || { color: "#6B7280", bg: "#EEF4F7" });

export default function Rider() {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [pod, setPod]         = useState({ notes: "", confirmed: false });
  const [updating, setUpdating] = useState(false);
  const [msg, setMsg]         = useState("");

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    setLoading(true);
    const data = await getRiderShipments();
    if (data?.success) setOrders(data.shipments);
    setLoading(false);
  };

  const openModal = (order) => {
    setModal(order);
    setPod({ notes: "", confirmed: false });
    setMsg("");
  };

  const handleUpdate = async (status) => {
    if (status === "delivered" && !pod.confirmed) return;
    setUpdating(true);
    const data = await updateShipmentStatus(modal.awb, status, pod.notes, null);
    setUpdating(false);
    if (data?.success) {
      setMsg("✅ Status updated!");
      setModal(null);
      loadOrders();
    } else {
      setMsg(data?.message || "Update failed");
    }
  };

  const counts = {
    assigned: orders.length,
    inTransit: orders.filter(o => o.status === "in_transit" || o.status === "out_for_delivery").length,
    delivered: 0, // Already filtered out on backend
  };

  if (loading) return <div style={{ background: "#F7FAFB", minHeight: "100vh" }}><div className="spinner" /></div>;

  return (
    <div style={{ background: "#F7FAFB", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#0B1E3D,#1A3460)", padding: "36px 24px 48px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <span className="tag tag-dark" style={{ marginBottom: 10, display: "inline-block" }}>RIDER PORTAL</span>
            <h1 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 28, color: "#fff" }}>My Deliveries</h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.08)", padding: "8px 14px", borderRadius: 24, border: "1px solid rgba(255,255,255,.12)" }}>
            <span className="live-dot" />
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "rgba(255,255,255,.8)" }}>ACTIVE</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 24px" }}>
        {/* Summary cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginTop: -24, marginBottom: 24 }}>
          {[["Assigned", counts.assigned, "#00C6D7"], ["In Transit", counts.inTransit, "#D97706"], ["Done Today", counts.delivered, "#16A34A"]].map(([l, v, c]) => (
            <div key={l} className="card" style={{ textAlign: "center", padding: "16px 12px", boxShadow: "0 4px 16px rgba(0,0,0,.08)" }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 30, color: c }}>{v}</div>
              <div style={{ color: "#6B7280", fontSize: 12, marginTop: 4 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Orders */}
        {orders.length === 0 ? (
          <div className="card" style={{ textAlign: "center", padding: "48px 24px" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🎉</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 18, color: "#0B1E3D", marginBottom: 8 }}>All caught up!</div>
            <div style={{ color: "#6B7280", fontSize: 14 }}>No active deliveries assigned to you right now.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingBottom: 32 }}>
            {orders.map(o => {
              const s = statusStyle(o.status);
              return (
                <div key={o.awb} className="card">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#9CA3AF", marginBottom: 4 }}>{o.awb}</div>
                      <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 18, color: "#0B1E3D" }}>{o.recipient_name}</div>
                    </div>
                    <span style={{ padding: "4px 12px", borderRadius: 20, fontWeight: 600, fontSize: 12, background: s.bg, color: s.color, textTransform: "capitalize" }}>
                      {o.status?.replace(/_/g, " ")}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
                    {[["📍 Address", o.recipient_address], ["📞 Phone", o.recipient_phone], ["⚖️ Weight", o.weight_kg ? `${o.weight_kg}kg` : "—"], ["📝 Notes", o.notes || "—"]].map(([k, v]) => (
                      <div key={k}>
                        <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 2 }}>{k}</div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  <div className="divider" />
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
                    <a href={`tel:${o.recipient_phone}`} className="btn btn-ghost btn-sm">📞 Call</a>
                    <a href={`https://wa.me/${(o.recipient_phone || "").replace(/^0/, "234")}?text=Hello%2C%20I%27m%20your%20Pulse%20Logistics%20rider.`}
                      target="_blank" rel="noopener noreferrer" className="btn btn-wa btn-sm">💬 WhatsApp</a>
                    <button className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }} onClick={() => openModal(o)}>
                      Log Update →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Update Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 18, color: "#0B1E3D", marginBottom: 4 }}>Update Delivery</div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#6B7280", marginBottom: 20 }}>
              {modal.awb} · {modal.recipient_name}
            </div>

            <div className="form-group">
              <label>Delivery Notes / POD</label>
              <textarea value={pod.notes} onChange={e => setPod(p => ({ ...p, notes: e.target.value }))} rows={3} placeholder="Describe the delivery outcome…" />
            </div>

            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 24 }}>
              <input type="checkbox" checked={pod.confirmed} onChange={e => setPod(p => ({ ...p, confirmed: e.target.checked }))} style={{ width: "auto", marginTop: 2, accentColor: "#00C6D7" }} />
              <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.55 }}>
                I confirm delivery was completed and the recipient acknowledged.
              </span>
            </label>

            {msg && <div className={`alert ${msg.startsWith("✅") ? "alert-success" : "alert-error"}`}>{msg}</div>}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <button className="btn btn-ghost" style={{ justifyContent: "center" }} onClick={() => handleUpdate("in_transit")} disabled={updating}>
                📍 In Transit
              </button>
              <button className="btn btn-primary" style={{ justifyContent: "center" }}
                disabled={!pod.confirmed || updating} onClick={() => handleUpdate("delivered")}>
                ✅ Mark Delivered
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
